import React, { useState } from 'react';
import { Property } from '../../types';
import {
  propertyAccentColor,
  propertyProgramCategoryLabel,
} from '../../utils/programLabels';

interface ProgramInfo {
  name: string;
  location: string;
  image: string;
  deliveryDate: string;
}

interface MapProgramLotsCardProps {
  program: ProgramInfo;
  lots: Property[];
  isProgramHighlighted?: boolean;
  selectedLotId?: string | null;
  onLotClick: (lot: Property) => void;
  className?: string;
}

const formatPrice = (price: number) =>
  new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(price);

const getLotTypeLabel = (lot: Property) =>
  lot.rooms ? `T${lot.rooms} · ${lot.surface} m²` : `${lot.surface} m²`;

const getLotFloor = (lot: Property) => {
  const floors = ['RDC', '1er', '2ème', '3ème', '4ème', '5ème'];
  return floors[parseInt(lot.id, 10) % floors.length];
};

const getLotOrientation = (lot: Property) => {
  const orientations = ['Nord', 'Sud', 'Est', 'Ouest'];
  return orientations[(parseInt(lot.id, 10) + 1) % orientations.length];
};

const MapProgramLotsCard: React.FC<MapProgramLotsCardProps> = ({
  program,
  lots,
  isProgramHighlighted = false,
  selectedLotId = null,
  onLotClick,
  className = '',
}) => {
  const [showAllLots, setShowAllLots] = useState(false);
  const accent = propertyAccentColor(lots[0]?.type ?? 'appartement');
  const prices = lots.map((l) => l.price).filter((p) => p > 0);
  const priceFrom = prices.length > 0 ? Math.min(...prices) : null;
  const visibleLots = showAllLots ? lots : lots.slice(0, 4);

  return (
    <article
      className={`overflow-hidden rounded-xl border bg-white shadow-[0_4px_20px_-4px_rgba(43,47,51,0.1)] transition-shadow ${
        isProgramHighlighted
          ? 'border-primary-400 ring-2 ring-primary-500/30'
          : 'border-neutral-200'
      } ${className}`}
    >
      {/* En-tête programme */}
      <div className="border-b border-neutral-100 bg-gradient-to-b from-neutral-50 to-white p-4">
        <div className="flex gap-3">
          <div className="relative h-20 w-20 shrink-0 overflow-hidden rounded-lg ring-1 ring-neutral-200">
            <img
              src={program.image || 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=400&h=400&fit=crop'}
              alt=""
              className="h-full w-full object-cover"
              loading="lazy"
            />
            <span
              className="absolute left-1 top-1 rounded px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-white"
              style={{ backgroundColor: accent }}
            >
              {propertyProgramCategoryLabel(lots[0]?.type ?? 'appartement')}
            </span>
          </div>

          <div className="min-w-0 flex-1">
            <h4
              className="font-display text-sm font-bold uppercase leading-snug tracking-wide line-clamp-2"
              style={{ color: accent }}
            >
              {program.name}
            </h4>
            <p className="mt-1 flex items-center gap-1 text-xs text-neutral-600">
              <svg className="h-3.5 w-3.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                />
              </svg>
              <span className="truncate">{program.location}</span>
            </p>
            <p className="mt-1 text-xs text-neutral-500">Livraison : {program.deliveryDate}</p>
            {priceFrom != null && (
              <p className="mt-2 text-sm font-bold tabular-nums text-primary-600">
                À partir de {formatPrice(priceFrom)}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Liste des lots */}
      <div className="bg-neutral-50/80 p-3">
        <p className="mb-2.5 px-0.5 text-xs font-semibold text-neutral-800">
          {lots.length} lot{lots.length > 1 ? 's' : ''} disponible{lots.length > 1 ? 's' : ''}
        </p>

        <ul className="flex flex-col gap-2">
          {visibleLots.map((lot) => {
            const isSelected = selectedLotId === lot.id;
            return (
              <li key={lot.id}>
                <button
                  type="button"
                  onClick={() => onLotClick(lot)}
                  className={`flex w-full flex-col gap-2 rounded-lg border bg-white p-3 text-left transition-all hover:shadow-sm ${
                    isSelected
                      ? 'border-primary-400 ring-2 ring-primary-500/25'
                      : 'border-neutral-200 hover:border-neutral-300'
                  }`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-sm font-bold text-neutral-900">{getLotTypeLabel(lot)}</p>
                      <p className="mt-0.5 text-[11px] font-medium uppercase tracking-wide text-neutral-500">
                        {propertyProgramCategoryLabel(lot.type)}
                      </p>
                    </div>
                    <p className="shrink-0 text-sm font-bold tabular-nums text-neutral-900">
                      {formatPrice(lot.price)}
                    </p>
                  </div>

                  <div className="flex flex-wrap gap-2 text-[11px] text-neutral-600">
                    <span className="rounded-md bg-neutral-100 px-2 py-0.5 font-medium">
                      Étage {getLotFloor(lot)}
                    </span>
                    <span className="rounded-md bg-neutral-100 px-2 py-0.5 font-medium">
                      {getLotOrientation(lot)}
                    </span>
                    {lot.features.some((f) => /parking|garage/i.test(f)) && (
                      <span className="rounded-md bg-primary-50 px-2 py-0.5 font-medium text-primary-700">
                        Parking
                      </span>
                    )}
                  </div>

                  <span
                    className="self-end text-[11px] font-semibold"
                    style={{ color: accent }}
                  >
                    Voir sur la carte →
                  </span>
                </button>
              </li>
            );
          })}
        </ul>

        {lots.length > 4 && (
          <button
            type="button"
            onClick={() => setShowAllLots((v) => !v)}
            className="mt-2.5 w-full rounded-lg border border-neutral-200 bg-white py-2 text-xs font-semibold text-neutral-700 transition-colors hover:bg-neutral-100"
          >
            {showAllLots ? 'Voir moins de lots' : `Voir les ${lots.length - 4} autres lots`}
          </button>
        )}
      </div>
    </article>
  );
};

export default MapProgramLotsCard;
